import { BoardEntity } from '../../../stores/boardStore';

/**
 * Strategy 1: Granular Matching
 * Prioritizes exactly as it does now: Ad > AdSet > Campaign > Source
 */
export const findGranularMatch = (traffic: any, entities: BoardEntity[] | undefined): BoardEntity | null => {
    if (!traffic || !entities) return null;

    const adId = traffic.ad_id ? String(traffic.ad_id) : null;
    const adsetId = traffic.adset_id ? String(traffic.adset_id) : null;
    const campaignId = traffic.campaign_id ? String(traffic.campaign_id) : (traffic.utm_id ? String(traffic.utm_id) : null);
    const genericSource = traffic.source ? String(traffic.source) : null;

    let matchedEntity: BoardEntity | null = null;

    // 1. Precise ID Matching by Type
    if (adId) {
        matchedEntity = entities.find(e =>
            (e.entityType === 'ad' || (e as any).type === 'ad') &&
            String(e.externalId) === adId
        ) || null;
    }

    if (!matchedEntity && adsetId) {
        matchedEntity = entities.find(e =>
            (['adset', 'ad_set', 'adgroup', 'ad_group'].includes(e.entityType || '')) &&
            String(e.externalId) === adsetId
        ) || null;
    }

    if (!matchedEntity && campaignId) {
        matchedEntity = entities.find(e =>
            (['campaign', 'campaign_group'].includes(e.entityType || '')) &&
            String(e.externalId) === campaignId
        ) || null;
    }

    // 2. Direct externalId match (as fallback if type failed or type unknown)
    if (!matchedEntity) {
        const primaryId = adId || adsetId || campaignId || genericSource;
        if (primaryId) {
            matchedEntity = entities.find(e => String(e.externalId) === primaryId) || null;
        }
    }

    // 3. Metadata bridge match (if primaryId didn't match externalId directly)
    if (!matchedEntity) {
        const primaryId = adId || adsetId || campaignId || genericSource;
        if (primaryId) {
            matchedEntity = entities.find(e => {
                const tm = e.traffic;
                if (!tm) return false;
                return String(tm.source) === primaryId || String(tm.utm_campaign) === primaryId;
            }) || null;
        }
    }

    return matchedEntity;
};

/**
 * Strategy 2: Campaign-Level Matching
 * If it matches the ad or adset, it goes up the hierarchy to find the campaign.
 */
export const findCampaignMatch = (traffic: any, entities: BoardEntity[] | undefined): BoardEntity | null => {
    const granularMatch = findGranularMatch(traffic, entities);
    if (!granularMatch) return null;

    // If it's already a campaign, return it
    if (['campaign', 'campaign_group'].includes(granularMatch.entityType || '')) {
        return granularMatch;
    }

    // If it's an ad or ad set, find its campaign parent in the hierarchy
    let current = granularMatch;
    let failsafe = 0;

    // We traverse up until we find a campaign or hit the root
    while (current && !['campaign', 'campaign_group'].includes(current.entityType || '') && failsafe < 10) {
        failsafe++;
        if (!current.parentId) break;

        const parent = entities?.find(e => String(e.id) === String(current.parentId));
        if (parent) {
            current = parent;
        } else {
            break;
        }
    }

    return current;
};
